// Small pieces the console pages share.
import { words } from '@qor/web-shared';
import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';

/** The page's heading: where it sits in the console, its title, then (on the right) what can be done from it. */
export function PageHeading({
  title,
  trail = [],
  actions,
}: {
  title: string;
  trail?: [label: string, to: string][];
  actions?: ReactNode;
}) {
  return (
    <div className="qc-page-heading">
      <div>
        <h1>{title}</h1>
        <p className="qc-breadcrumb">
          QOR Console <span aria-hidden="true">&rsaquo;</span>{' '}
          {trail.map(([label, to]) => (
            <span key={to}>
              <Link to={to}>{label}</Link> <span aria-hidden="true">&rsaquo;</span>{' '}
            </span>
          ))}
          <strong>{title}</strong>
        </p>
      </div>
      {actions && <div className="qc-page-actions">{actions}</div>}
    </div>
  );
}

/** The panel shown instead of a page's data until internal staff choose a processor. */
export function ChooseProcessor({ what }: { what: string }) {
  return (
    <div className="qc-panel">
      <p className="qc-empty">Choose a processor above {what}. Processors are never mixed together.</p>
    </div>
  );
}

/** The result line after a post: qc-auth-success / qc-auth-error (CardholderDetail, PointsConfiguration, ...). */
export function ResultBanner({ message, ok }: { message: string | null; ok: boolean }) {
  if (!message) return null;
  return (
    <div className={ok ? 'qc-auth-success' : 'qc-auth-error'} role="status">
      {message}
    </div>
  );
}

/** The result line of PartnerDetail and UserDetail (coloured text). */
export function ResultText({ message, ok }: { message: string | null; ok: boolean }) {
  if (!message) return null;
  return (
    <p className={`qc-result-text ${ok ? 'qc-result-text-ok' : ''}`} role="status">
      {message}
    </p>
  );
}

/** A create form's error (Add cardholder, Add partner, Add user). */
export const FormError = ({ message }: { message: string | null }) =>
  message ? (
    <p className="qc-result-text" role="alert">
      {message}
    </p>
  ) : null;

/** Waiting on someone (amber), refused or stopped (red); anything else that is not the good status is neutral. */
const statusTone = (status: string) =>
  /^(New|Pending)/.test(status)
    ? 'qc-badge-warning'
    : /^(Suspended|Declined|Failed|Rejected)/.test(status)
      ? 'qc-badge-danger'
      : 'qc-badge-muted';

export const StatusBadge = ({ status, good = 'Active' }: { status: string; good?: string }) => (
  <span className={`qc-badge ${status === good ? 'qc-badge-success' : statusTone(status)}`}>{words(status)}</span>
);

/** "Page n of m · total" with Previous / Next links that keep the filters. */
export function Pagination({
  page,
  totalPages,
  totalCount,
  href,
}: {
  page: number;
  totalPages: number;
  totalCount: number;
  href: (page: number) => string;
}): ReactNode {
  if (totalPages <= 1) return null;
  return (
    <div className="qc-pagination">
      <span>
        Page {page} of {totalPages} &middot; {totalCount} total
      </span>
      <div className="qc-pagination-links">
        {page > 1 && (
          <Link className="qc-button-ghost" to={href(page - 1)}>
            Previous
          </Link>
        )}
        {page < totalPages && (
          <Link className="qc-button-ghost" to={href(page + 1)}>
            Next
          </Link>
        )}
      </div>
    </div>
  );
}

/** Confirm, as the legacy forms' onsubmit="return confirm(...)". */
export const confirmed = (question: string) => window.confirm(question);
